import { Combobox } from "@dashboard/components/Combobox";
import RichTextEditor from "@dashboard/components/RichTextEditor";
import { useAllPageTypesQuery, usePageCreateMutation, usePageListQuery, usePageUpdateMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { getParsedDataForJsonStringField } from "@dashboard/utils/richText/misc";
import { RichTextEditorRef } from "@dashboard/utils/richText/types";
import { Box, Button, Checkbox, Input, Skeleton, Text } from "@saleor/macaw-ui-next";
import { Edit2, FileText } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { EmptyState } from "../../components/EmptyState";
import { Pagination } from "../../components/Pagination";
import { StepWrapper } from "../../components/StepWrapper";
import { useWizardContext } from "../../context/WizardContext";
import { getDefaultContentForPageType } from "./defaultContent";
import { messages } from "./messages";

const PAGE_SIZE = 10;

const CONTENT_PAGES_FORM_ID = "wizard-content-pages-form";

export const ContentPagesStep: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const { completeCurrentStep, goToNextStep } = useWizardContext();

  const editorRef = useRef<RichTextEditorRef | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [pageTypeId, setPageTypeId] = useState("");
  const [selectedPageTypeSlug, setSelectedPageTypeSlug] = useState<string | null>(null);
  const [isPublished, setIsPublished] = useState(true);
  const [titleError, setTitleError] = useState("");
  const [slugError, setSlugError] = useState("");
  const [pageTypeError, setPageTypeError] = useState("");
  const [paginationVars, setPaginationVars] = useState<{
    first?: number;
    after?: string;
    last?: number;
    before?: string;
  }>({ first: PAGE_SIZE });

  const { data, loading, refetch } = usePageListQuery({
    variables: paginationVars,
  });

  const { data: pageTypesData } = useAllPageTypesQuery();

  const currentDefaultContent = useMemo(() => {
    return getDefaultContentForPageType(selectedPageTypeSlug);
  }, [selectedPageTypeSlug, editorKey]);

  const [createPage, { loading: creating }] = usePageCreateMutation();
  const [updatePage, { loading: updating }] = usePageUpdateMutation();

  const pages = mapEdgesToItems(data?.pages) ?? [];
  const pagesPageInfo = data?.pages?.pageInfo;
  const pageTypes = mapEdgesToItems(pageTypesData?.pageTypes) ?? [];
  const pageTypeChoices = pageTypes.map(pt => ({
    label: pt.name,
    value: pt.id,
  }));

  const handleStepSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    completeCurrentStep();
    goToNextStep();
  };

  const resetForm = () => {
    setTitle("");
    setSlug("");
    setPageTypeId("");
    setSelectedPageTypeSlug(null);
    setIsPublished(true);
    setTitleError("");
    setSlugError("");
    setPageTypeError("");
    setEditingPageId(null);
    setEditorKey(prev => prev + 1);
    setShowForm(false);
  };

  const handleEdit = (page: any) => {
    setEditingPageId(page.id);
    setTitle(page.title);
    setSlug(page.slug);
    setPageTypeId(page.pageType?.id || "");
    setSelectedPageTypeSlug(page.pageType?.slug || null);
    setIsPublished(page.isPublished);
    setEditorKey(prev => prev + 1);
    setShowForm(true);
  };

  const handleSave = async () => {
    let hasError = false;

    if (!pageTypeId) {
      setPageTypeError(intl.formatMessage(messages.pageType) + " is required");
      hasError = true;
    }

    if (!title.trim()) {
      setTitleError(intl.formatMessage(messages.pageTitle) + " is required");
      hasError = true;
    }

    if (!slug.trim()) {
      setSlugError(intl.formatMessage(messages.urlSlug) + " is required");
      hasError = true;
    }

    if (hasError) return;

    let contentJson: string | undefined;

    if (editorRef.current) {
      const editorData = await editorRef.current.save();

      contentJson = getParsedDataForJsonStringField(editorData) ?? undefined;
    }

    let result;

    if (editingPageId) {
      // Update existing page
      result = await updatePage({
        variables: {
          id: editingPageId,
          input: {
            title: title.trim(),
            slug: slug.trim(),
            content: contentJson,
            isPublished,
          },
        },
      });
    } else {
      // Create new page
      result = await createPage({
        variables: {
          input: {
            title: title.trim(),
            slug: slug.trim(),
            content: contentJson,
            isPublished,
            pageType: pageTypeId,
          },
        },
      });
    }

    const errors = editingPageId 
      ? ((result.data as any)?.pageUpdate?.errors ?? [])
      : ((result.data as any)?.pageCreate?.errors ?? []);

    if (errors.length > 0) {
      notify({
        status: "error",
        text: errors[0]?.message ?? intl.formatMessage(messages.errorMessage),
      });

      return;
    }

    notify({
      status: "success",
      text: editingPageId 
        ? intl.formatMessage(messages.updateSuccessMessage) 
        : intl.formatMessage(messages.successMessage),
    });
    resetForm();
    refetch();
  };

  const isFormLoading = creating || updating;

  const renderForm = () => (
    <Box
      display="flex"
      flexDirection="column"
      gap={4}
      borderRadius={4}
      __backgroundColor="rgba(248, 250, 252, 0.7)"
      style={{
        padding: "clamp(16px, 2vw, 20px)",
        border: "1px solid rgba(148, 163, 184, 0.16)",
      }}
    >
      <Combobox
        value={pageTypeChoices.find(c => c.value === pageTypeId) ?? null}
        fetchOptions={() => undefined}
        onChange={e => {
          const newPageTypeId = e.target.value ?? "";

          setPageTypeId(newPageTypeId);

          const selectedType = pageTypes.find(pt => pt.id === newPageTypeId);
          const slug = selectedType?.slug ?? null;

          setSelectedPageTypeSlug(slug);
          setEditorKey(prev => prev + 1);

          if (pageTypeError) setPageTypeError("");
        }}
        options={pageTypeChoices}
        label={intl.formatMessage(messages.pageType)}
        error={!!pageTypeError}
        helperText={pageTypeError || intl.formatMessage(messages.pageTypeHelper)}
        disabled={isFormLoading || !!editingPageId}
        size="medium"
      />

      <Input
        label={intl.formatMessage(messages.pageTitle)}
        value={title}
        onChange={e => {
          setTitle(e.target.value);

          if (titleError) setTitleError("");
        }}
        error={!!titleError}
        helperText={titleError || intl.formatMessage(messages.pageTitleHelper)}
        disabled={isFormLoading}
        size="medium"
      />

      <Input
        label={intl.formatMessage(messages.urlSlug)}
        value={slug}
        onChange={e => {
          setSlug(e.target.value);

          if (slugError) setSlugError("");
        }}
        error={!!slugError}
        helperText={slugError || intl.formatMessage(messages.urlSlugHelper)}
        disabled={isFormLoading}
        size="medium"
      />

      <RichTextEditor
        key={editorKey}
        defaultValue={currentDefaultContent}
        editorRef={editorRef}
        onChange={() => {}}
        disabled={isFormLoading}
        error={false}
        helperText={intl.formatMessage(messages.pageContentHelper)}
        label={intl.formatMessage(messages.pageContent)}
        name="content"
      />
      <Box display="flex" gap={2} justifyContent="space-between" flexWrap="wrap" marginTop={2}>
        <Checkbox
          checked={isPublished}
          onCheckedChange={checked => setIsPublished(!!checked)}
          disabled={isFormLoading}
        >
          <Text>{intl.formatMessage(messages.publishImmediately)}</Text>
        </Checkbox>

        <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
          <Button variant="secondary" size="medium" onClick={resetForm} disabled={isFormLoading}>
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button variant="primary" size="medium" onClick={handleSave} disabled={isFormLoading}>
            {editingPageId ? intl.formatMessage(messages.update) : intl.formatMessage(messages.save)}
          </Button>
        </Box>
      </Box>

    </Box>
  );

  const renderList = () => (
    <>
      <Box
        display="flex"
        alignItems={{ mobile: "flex-start", tablet: "center" }}
        justifyContent="space-between"
        flexDirection={{ mobile: "column", tablet: "row" }}
        gap={3}
        marginBottom={4}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Text size={2} fontWeight="bold" color="default1" __textTransform="uppercase">
            Pages
          </Text>
          <Text size={2} color="default2">
            Review and manage the storefront pages used in your setup.
          </Text>
        </Box>
        <Button variant="primary" size="large" onClick={() => setShowForm(true)}>
          {intl.formatMessage(messages.addPage)}
        </Button>
      </Box>

      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          borderRadius={4}
          style={{
            padding: "clamp(16px, 2vw, 20px)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
          }}
        >
          <Skeleton __height="52px" />
          <Skeleton __height="52px" />
          <Skeleton __height="52px" />
        </Box>
      ) : pages.length === 0 ? (
        <EmptyState icon={FileText} message={intl.formatMessage(messages.emptyPages)} />
      ) : (
        <Box
          borderRadius={4}
          __overflow="hidden"
          style={{ border: "1px solid rgba(148, 163, 184, 0.16)" }}
        >
          <Box
            display="grid"
            gap={2}
            paddingTop={3}
            paddingBottom={3}
            __backgroundColor="rgba(148, 163, 184, 0.05)"
            style={{
              gridTemplateColumns: "minmax(0, 2fr) minmax(220px, 1.35fr) 120px 36px",
              paddingLeft: "16px",
              paddingRight: "16px",
              borderBottom: "1px solid rgba(148, 163, 184, 0.14)",
            }}
          >
            <Text size={2} fontWeight="bold" color="default2">
              {intl.formatMessage(messages.titleColumn)}
            </Text>
            <Text size={2} fontWeight="bold" color="default2">
              {intl.formatMessage(messages.slugColumn)}
            </Text>
            <Text size={2} fontWeight="bold" color="default2">
              {intl.formatMessage(messages.statusColumn)}
            </Text>
            <Box __width="32px" />
          </Box>

          <Box display={{ mobile: "block", tablet: "none" }}>
            {pages.map((page, index) => (
              <Box
                key={page.id}
                display="flex"
                flexDirection="column"
                gap={2}
                paddingY={3}
                style={{
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  borderBottom:
                    index < pages.length - 1 ? "1px solid rgba(148, 163, 184, 0.12)" : undefined,
                }}
              >
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Text size={3} fontWeight="medium" __flex={1}>
                    {page.title}
                  </Text>
                  <Box
                    as="button"
                    cursor="pointer"
                    onClick={() => handleEdit(page)}
                    __backgroundColor="transparent"
                    __padding="4px"
                    style={{ border: "none" }}
                    borderRadius={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    __flexShrink={0}
                  >
                    <Edit2 size={16} color="#3b82f6" />
                  </Box>
                </Box>
                <Text size={2} color="default2">
                  {page.slug}
                </Text>
                <Text
                  size={2}
                  __color={page.isPublished ? "#27ae60" : undefined}
                  color={page.isPublished ? undefined : "default2"}
                >
                  {page.isPublished
                    ? intl.formatMessage(messages.published)
                    : intl.formatMessage(messages.draft)}
                </Text>
              </Box>
            ))}
          </Box>

          <Box
            __display={{ mobile: "none", tablet: "block" }}
          >
            {pages.map((page, index) => (
              <Box
                key={page.id}
                display="grid"
                gap={2}
                paddingTop={3}
                paddingBottom={3}
                alignItems="center"
                style={{
                  gridTemplateColumns: "minmax(0, 2fr) minmax(220px, 1.35fr) 120px 36px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  borderBottom:
                    index < pages.length - 1 ? "1px solid rgba(148, 163, 184, 0.12)" : undefined,
                }}
              >
                <Text size={3} fontWeight="medium">
                  {page.title}
                </Text>
                <Text size={2} color="default2" display={{ mobile: "none", tablet: "block" }}>
                  {page.slug}
                </Text>
                <Text
                  size={2}
                  __color={page.isPublished ? "#27ae60" : undefined}
                  color={page.isPublished ? undefined : "default2"}
                >
                  {page.isPublished
                    ? intl.formatMessage(messages.published)
                    : intl.formatMessage(messages.draft)}
                </Text>
                <Box
                  as="button"
                  cursor="pointer"
                  onClick={() => handleEdit(page)}
                  __backgroundColor="transparent"
                  __padding="4px"
                  style={{ border: "none" }}
                  borderRadius={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Edit2 size={16} color="#3b82f6" />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {pagesPageInfo && (
        <Pagination
          hasNextPage={pagesPageInfo.hasNextPage}
          hasPreviousPage={pagesPageInfo.hasPreviousPage}
          onNext={() =>
            setPaginationVars({
              first: PAGE_SIZE,
              after: pagesPageInfo.endCursor ?? undefined,
            })
          }
          onPrevious={() =>
            setPaginationVars({
              last: PAGE_SIZE,
              before: pagesPageInfo.startCursor ?? undefined,
            })
          }
          disabled={loading}
        />
      )}
    </>
  );

  return (
    <StepWrapper
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      skippable={true}
      formId={CONTENT_PAGES_FORM_ID}
    >
      <form id={CONTENT_PAGES_FORM_ID} onSubmit={handleStepSubmit} />
      {showForm ? renderForm() : renderList()}
    </StepWrapper>
  );
};
